import type { ReactNode } from 'react';

import { Character } from '../../character/Character';
import { Decor } from './decor';
import { type Character as CharacterConfig } from '../../character/config';
import './stage.css';

/**
 * The venue: truss, screen, speakers, booth, a DJ and a crowd.
 *
 * ## Machine-converted, like the characters are
 *
 * The art comes from `dev/stage-and-character-v1.3` through
 * `scripts/convert-character.mjs` - the same script and the same ritual as the
 * avatar drops, checked the same way: convert the previous generator first and
 * confirm it comes back identical before pointing it at anything new. It did,
 * and the two bugs the stage found on the way through are fixed there rather
 * than worked around here.
 *
 * Two holes were cut in the scene, and they are the whole point of doing this.
 *
 * ## The DJ and the crowd are real people
 *
 * The scene shipped with a hand-simplified rig for the DJ and three master rigs
 * instanced eleven times for the audience, coloured through CSS custom
 * properties - which is what you build when you have no generator. We have one,
 * and its coordinate system is identical: the fan masters draw legs at
 * `rect x=84 y=192 w=22 h=46 rx=6`, which is `Character`'s back view exactly.
 *
 * So the `use` instances are gone and `Character` stands in their place at the
 * same transforms. The DJ is the host's own saved character and the crowd is
 * whoever is actually in the room - and every hairstyle, mask and chest print
 * works, including the ones added after this scene was drawn.
 *
 * ## The screen is deliberately empty, and the scene is in two halves
 *
 * `layer-screen` draws the bezel and the glass and nothing else. What goes on
 * it - artwork, a stream, a Twitch embed - is ordinary HTML positioned over
 * this by `LiveStage`, because a `video` inside an SVG is a compatibility
 * problem with no upside.
 *
 * Which creates one problem worth the split: the DJ's head is at y474-601 and
 * the screen ends at y582, so in the original drawing the head overlaps the
 * bottom of the screen and is drawn on top of it. An HTML overlay is on top of
 * the whole SVG, so it covered them - a decapitated host behind an opaque
 * rectangle.
 *
 * So `back` is everything up to and including the screen bezel, `front` is the
 * DJ forward, and `LiveStage` stacks them with the screen in between. Two SVGs
 * with the same viewBox, absolutely positioned on each other; the layer order
 * of the original is preserved exactly, with one HTML element spliced into it.
 */

/**
 * Where a fan stands. The positions are the scene's.
 *
 * No animation here any more. Everybody idles, and what they are *doing* comes
 * from the room - see `useRoomReactions`. Assigning a dance to the third slot
 * meant the person standing there was dancing because of where they stood,
 * which is not a thing that happens in a room; it also had somebody clapping
 * permanently, seen from behind, which was as odd as it sounds.
 *
 * `stagger` stays, because it is the opposite kind of thing: three offsets on
 * one breathing loop, so an idle crowd is an audience rather than a chorus
 * line.
 */
const CROWD_MID = [
  { at: 'translate(330,842) scale(0.7)', stagger: 1 },
  { at: 'translate(830,836) scale(0.72)', stagger: 2 },
  { at: 'translate(1560,844) scale(0.7)', stagger: 0 },
] as const;

/*
 * Listed from the middle outwards, and the front row is filled first.
 *
 * Both are about a quiet room, which is the normal one. Filling in scene order
 * would put the first two people who walk in at the very back and half off the
 * left edge - so a broadcast with two listeners would draw them as a smudge in
 * the corner and leave the middle of the stage empty. Centre-out means a room
 * with anybody in it looks like a room with somebody in front of the decks.
 */
const CROWD_FRONT = [
  { at: 'translate(930,878) scale(1.05)', stagger: 0 },
  { at: 'translate(690,868) scale(1.08)', stagger: 2 },
  { at: 'translate(1175,890) scale(0.98)', stagger: 1 },
  { at: 'translate(455,892) scale(0.95)', stagger: 1 },
  { at: 'translate(1420,870) scale(1.06)', stagger: 2 },
  { at: 'translate(210,872) scale(1.05)', stagger: 2 },
  { at: 'translate(1660,884) scale(1.0)', stagger: 0 },
  { at: 'translate(-30,886) scale(1.0)', stagger: 0 },
] as const;

/**
 * How many people the scene has room for.
 *
 * Exported because the room only needs to fetch this many characters, and
 * asking for a hundred to draw eleven is a request nobody should make.
 */
export const STAGE_CAPACITY = CROWD_MID.length + CROWD_FRONT.length;

/**
 * Where the back row starts in the list.
 *
 * The front row is `0` to here, so the viewer at index zero is centre-front -
 * which is the point. Anywhere in the back row they are small, dimmed, blurred
 * and behind somebody, which is a poor way to be shown yourself in a room you
 * have just walked into.
 */
export const STAGE_FRONT_AT = 0;

/** One person in the crowd, seen from behind. */
function Fan({
  at,
  config,
  anim,
  stagger,
  name,
}: {
  at: string;
  config: CharacterConfig;
  anim: string;
  stagger: number;
  /** A chip over their head, for the one fan who is the viewer. */
  name?: string | undefined;
}) {
  return (
    <g transform={at}>
      {/*
        A nested `svg`, which is what `Character` renders and what the `use` it
        replaces resolved to: the same 220x272 box at the same origin with no
        scaling of its own. The transform above does the placing.
      */}
      <Character config={config} view="back" crop="full" size={220} anim={anim} stagger={stagger} />

      {name !== undefined && (
        <g>
          <rect
            x="52"
            y="-16"
            width="116"
            height="30"
            rx="15"
            fill="#151530"
            stroke="#2B2B2B"
            strokeWidth="2"
          />
          <text
            x="110"
            y="5"
            data-fan-name
            textAnchor="middle"
            fontFamily="Helvetica, Arial, sans-serif"
            fontSize="16"
            fontWeight="700"
            fill="#9AA1F7"
          >
            {name}
          </text>
        </g>
      )}
    </g>
  );
}

export function StageScene({
  layer,
  host,
  crowd,
  theme = 'default',
  children,
}: {
  /**
   * Which part of the drawing.
   *
   * Four, and the order they are listed in is the order they paint - which is
   * the original drawing's order exactly, with two things spliced into it.
   *
   *   `sky`       the two full-bleed gradients, and nothing else
   *   `back`      truss, lights, the screen bezel
   *   `front`     the host, the booth, the stage platform
   *   `audience`  the painted blob rows, alone so they can be tiled sideways
   *   `fans`      the real people, who stand in front of the painted ones
   *
   * The screen goes between `back` and `front` because the host's head overlaps
   * it; the tiled crowd goes between `front` and `fans` because it is painted
   * over the platform and stood in front of by everybody real.
   */
  layer: 'sky' | 'back' | 'front' | 'audience' | 'fans';
  /** The host's character, behind the decks. */
  host: CharacterConfig;
  /**
   * Who is in the room, in the order they should stand.
   *
   * Short is normal and fine. The scene draws as many as it has places for and
   * leaves the rest of the row to the painted blobs behind it, which is what a
   * half-full room looks like anyway - and a great deal better than eleven
   * invented people in a room with two listeners in it.
   */
  crowd: { config: CharacterConfig; name?: string; anim?: string }[];
  /**
   * Which season the room is dressed for, from `config/stage-themes.php`.
   *
   * Passed down rather than read here: the scene is drawn five times over for
   * one room - once per layer - and every copy has to agree about the date.
   */
  theme?: string;
  /** Anything that belongs inside the SVG, drawn last. */
  children?: ReactNode;
}) {
  return (
    <svg
      viewBox="0 0 1920 1080"
      /*
        The sky is the one layer that is stretched rather than fitted - see the
        note on `layer`. Everything else keeps its aspect, because everything
        else has perspective in it.
      */
      preserveAspectRatio={layer === 'sky' ? 'none' : undefined}
      className="block h-full w-full"
      data-stage-scene
      /*
        Decoration, and labelled as such.

        Everything this says - who is on, what is playing, how many are here -
        is text in the panel around it. A screen reader walking eleven unnamed
        figures and a truss is being read the set dressing instead of the room.
      */
      aria-hidden
    >
      {layer === 'sky' ? (
        /*
          The room the stage is in.
          
          Two full-bleed rects and nothing else, which is what makes this the
          one layer that can be stretched: a vertical gradient does not care how
          wide it is drawn, and the glow becoming a wider ellipse is what a
          wider room should look like anyway. `LiveStage` draws it across the
          whole container with `preserveAspectRatio="none"` so the backdrop
          reaches the edges instead of stopping where the 16:9 box does.
        */
        <g id="layer-background">
          <rect width="1920" height="1080" fill="url(#sky)" />
          <rect width="1920" height="1080" fill="url(#stageGlow)" />
        </g>
      ) : layer === 'audience' ? (
        /*
          The painted crowd, on its own.

          Nothing here has perspective or a fixed place on the stage - it is
          rows of blobs on a dark ground - which is what makes it the one part
          of the scene that can be repeated sideways without looking repeated.
        */
      <g id="layer-audience">
        <circle cx="-11" cy="942" r="26" fill="#141414" />
        <rect x="-34" y="870" width="10" height="52" rx="5" fill="#141414" transform="rotate(14 -29 942)" />
        <circle cx="77" cy="954" r="26" fill="#141414" />
        <circle cx="142" cy="949" r="26" fill="#141414" />
        <circle cx="230" cy="944" r="26" fill="#141414" />
        <rect x="243" y="872" width="10" height="52" rx="5" fill="#141414" transform="rotate(-14 248 944)" />
        <circle cx="294" cy="956" r="26" fill="#141414" />
        <circle cx="359" cy="951" r="26" fill="#141414" />
        <circle cx="447" cy="946" r="26" fill="#141414" />
        <rect x="424" y="874" width="10" height="52" rx="5" fill="#141414" transform="rotate(14 429 946)" />
        <circle cx="512" cy="958" r="26" fill="#141414" />
        <circle cx="600" cy="953" r="26" fill="#141414" />
        <circle cx="665" cy="948" r="26" fill="#141414" />
        <rect x="678" y="876" width="10" height="52" rx="5" fill="#141414" transform="rotate(-14 683 948)" />
        <circle cx="729" cy="943" r="26" fill="#141414" />
        <circle cx="817" cy="955" r="26" fill="#141414" />
        <circle cx="882" cy="950" r="26" fill="#141414" />
        <rect x="859" y="878" width="10" height="52" rx="5" fill="#141414" transform="rotate(14 864 950)" />
        <circle cx="970" cy="945" r="26" fill="#141414" />
        <circle cx="1035" cy="957" r="26" fill="#141414" />
        <circle cx="1100" cy="952" r="26" fill="#141414" />
        <rect x="1113" y="880" width="10" height="52" rx="5" fill="#141414" transform="rotate(-14 1118 952)" />
        <circle cx="1188" cy="947" r="26" fill="#141414" />
        <circle cx="1252" cy="942" r="26" fill="#141414" />
        <circle cx="1340" cy="954" r="26" fill="#141414" />
        <rect x="1317" y="882" width="10" height="52" rx="5" fill="#141414" transform="rotate(14 1322 954)" />
        <circle cx="1405" cy="949" r="26" fill="#141414" />
        <circle cx="1470" cy="944" r="26" fill="#141414" />
        <circle cx="1558" cy="956" r="26" fill="#141414" />
        <rect x="1571" y="884" width="10" height="52" rx="5" fill="#141414" transform="rotate(-14 1576 956)" />
        <circle cx="1623" cy="951" r="26" fill="#141414" />
        <circle cx="1687" cy="946" r="26" fill="#141414" />
        <circle cx="1775" cy="958" r="26" fill="#141414" />
        <rect x="1752" y="886" width="10" height="52" rx="5" fill="#141414" transform="rotate(14 1757 958)" />
        <circle cx="1840" cy="953" r="26" fill="#141414" />
        <circle cx="1928" cy="948" r="26" fill="#141414" />
        <circle cx="4" cy="1001" r="30" fill="#101010" />
        <circle cx="82" cy="996" r="30" fill="#101010" />
        <circle cx="184" cy="1008" r="30" fill="#101010" />
        <rect x="161" y="932" width="10" height="52" rx="5" fill="#101010" transform="rotate(14 166 1008)" />
        <circle cx="262" cy="1003" r="30" fill="#101010" />
        <circle cx="340" cy="998" r="30" fill="#101010" />
        <circle cx="441" cy="993" r="30" fill="#101010" />
        <rect x="454" y="917" width="10" height="52" rx="5" fill="#101010" transform="rotate(-14 459 993)" />
        <circle cx="520" cy="1005" r="30" fill="#101010" />
        <circle cx="621" cy="1000" r="30" fill="#101010" />
        <circle cx="699" cy="995" r="30" fill="#101010" />
        <rect x="676" y="919" width="10" height="52" rx="5" fill="#101010" transform="rotate(14 681 995)" />
        <circle cx="777" cy="1007" r="30" fill="#101010" />
        <circle cx="879" cy="1002" r="30" fill="#101010" />
        <circle cx="957" cy="997" r="30" fill="#101010" />
        <rect x="970" y="921" width="10" height="52" rx="5" fill="#101010" transform="rotate(-14 975 997)" />
        <circle cx="1058" cy="992" r="30" fill="#101010" />
        <circle cx="1137" cy="1004" r="30" fill="#101010" />
        <circle cx="1215" cy="999" r="30" fill="#101010" />
        <rect x="1192" y="923" width="10" height="52" rx="5" fill="#101010" transform="rotate(14 1197 999)" />
        <circle cx="1316" cy="994" r="30" fill="#101010" />
        <circle cx="1394" cy="1006" r="30" fill="#101010" />
        <circle cx="1473" cy="1001" r="30" fill="#101010" />
        <rect x="1486" y="925" width="10" height="52" rx="5" fill="#101010" transform="rotate(-14 1491 1001)" />
        <circle cx="1574" cy="996" r="30" fill="#101010" />
        <circle cx="1652" cy="1008" r="30" fill="#101010" />
        <circle cx="1753" cy="1003" r="30" fill="#101010" />
        <rect x="1730" y="927" width="10" height="52" rx="5" fill="#101010" transform="rotate(14 1735 1003)" />
        <circle cx="1832" cy="998" r="30" fill="#101010" />
        <circle cx="1910" cy="993" r="30" fill="#101010" />
        <circle className="tw" cx="90" cy="926" r="4.5" fill="#7B84F2" opacity="0.9" />
        <circle className="tw t1" cx="227" cy="979" r="4.5" fill="#33E58A" opacity="0.9" />
        <circle className="tw t2" cx="364" cy="1032" r="4.5" fill="#FF3F4A" opacity="0.9" />
        <circle className="tw t3" cx="501" cy="965" r="4.5" fill="#F2F2F2" opacity="0.9" />
        <circle className="tw t1" cx="638" cy="1018" r="4.5" fill="#7B84F2" opacity="0.9" />
        <circle className="tw" cx="775" cy="951" r="4.5" fill="#33E58A" opacity="0.9" />
        <circle className="tw t2" cx="912" cy="1004" r="4.5" fill="#FF3F4A" opacity="0.9" />
        <circle className="tw t3" cx="1049" cy="937" r="4.5" fill="#F2F2F2" opacity="0.9" />
        <circle className="tw t1" cx="1186" cy="990" r="4.5" fill="#7B84F2" opacity="0.9" />
        <circle className="tw" cx="1323" cy="1043" r="4.5" fill="#33E58A" opacity="0.9" />
        <circle className="tw t2" cx="1460" cy="976" r="4.5" fill="#FF3F4A" opacity="0.9" />
        <circle className="tw t3" cx="1597" cy="1029" r="4.5" fill="#F2F2F2" opacity="0.9" />
        <circle className="tw t1" cx="1734" cy="962" r="4.5" fill="#7B84F2" opacity="0.9" />
        <circle className="tw t2" cx="111" cy="1015" r="4.5" fill="#33E58A" opacity="0.9" />
      </g>
      ) : layer === 'back' ? (
        <>
      <defs>
          <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#151515" />
            <stop offset="0.55" stopColor="#0D0D0D" />
            <stop offset="1" stopColor="#0B0B0B" />
          </linearGradient>
          <radialGradient id="stageGlow" cx="0.5" cy="0.62" r="0.7">
            <stop offset="0" stopColor="#7B84F2" stopOpacity="0.16" />
            <stop offset="0.55" stopColor="#7B84F2" stopOpacity="0.05" />
            <stop offset="1" stopColor="#7B84F2" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="screenFill" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#151515" />
            <stop offset="1" stopColor="#0E0E0E" />
          </linearGradient>
      </defs>

      <Decor theme={theme} slot="sky" />

      <g id="layer-truss">
        <rect x="120" y="40" width="1680" height="18" rx="9" fill="#1B1B1B" stroke="#2B2B2B" strokeWidth="2" />
        <rect x="120" y="40" width="18" height="780" rx="9" fill="#1B1B1B" stroke="#2B2B2B" strokeWidth="2" />
        <rect x="1782" y="40" width="18" height="780" rx="9" fill="#1B1B1B" stroke="#2B2B2B" strokeWidth="2" />
        <rect x="234" y="58" width="52" height="38" rx="8" fill="#1B1B1B" stroke="#2B2B2B" />
        <circle cx="260" cy="96" r="10" fill="#7B84F2" />
        <rect x="494" y="58" width="52" height="38" rx="8" fill="#1B1B1B" stroke="#2B2B2B" />
        <circle cx="520" cy="96" r="10" fill="#FF3F4A" />
        <rect x="754" y="58" width="52" height="38" rx="8" fill="#1B1B1B" stroke="#2B2B2B" />
        <circle cx="780" cy="96" r="10" fill="#33E58A" />
        <rect x="1114" y="58" width="52" height="38" rx="8" fill="#1B1B1B" stroke="#2B2B2B" />
        <circle cx="1140" cy="96" r="10" fill="#8E7BF2" />
        <rect x="1374" y="58" width="52" height="38" rx="8" fill="#1B1B1B" stroke="#2B2B2B" />
        <circle cx="1400" cy="96" r="10" fill="#5FD0B0" />
        <rect x="1634" y="58" width="52" height="38" rx="8" fill="#1B1B1B" stroke="#2B2B2B" />
        <circle cx="1660" cy="96" r="10" fill="#7B84F2" />
      </g>
      <g id="layer-lights">
        <polygon className="beam" points="246,96 274,96 785,760 365,760" fill="#7B84F2" opacity="0.13" />
        <polygon className="beam" points="255,96 265,96 648.5,760 501.5,760" fill="#7B84F2" opacity="0.16" />
        <polygon className="beam b1" points="506,96 534,96 988,760 448,760" fill="#FF3F4A" opacity="0.13" />
        <polygon className="beam b1" points="515,96 525,96 812.5,760 623.5,760" fill="#FF3F4A" opacity="0.16" />
        <polygon className="beam b2" points="766,96 794,96 1191,760 531,760" fill="#33E58A" opacity="0.13" />
        <polygon className="beam b2" points="775,96 785,96 976.5,760 745.5,760" fill="#33E58A" opacity="0.16" />
        <polygon className="beam b3" points="1126,96 1154,96 1269,760 849,760" fill="#8E7BF2" opacity="0.13" />
        <polygon className="beam b3" points="1135,96 1145,96 1132.5,760 985.5,760" fill="#8E7BF2" opacity="0.16" />
        <polygon className="beam b1" points="1386,96 1414,96 1472,760 932,760" fill="#5FD0B0" opacity="0.13" />
        <polygon className="beam b1" points="1395,96 1405,96 1296.5,760 1107.5,760" fill="#5FD0B0" opacity="0.16" />
        <polygon className="beam b2" points="1646,96 1674,96 1675,760 1015,760" fill="#7B84F2" opacity="0.13" />
        <polygon className="beam b2" points="1655,96 1665,96 1460.5,760 1229.5,760" fill="#7B84F2" opacity="0.16" />
      </g>
      <Decor theme={theme} slot="air" />

      <g id="layer-screen">
        <rect x="512" y="80" width="896" height="518" rx="22" fill="#161616" stroke="#2B2B2B" strokeWidth="3" />
        <rect x="528" y="96" width="864" height="486" rx="12" fill="url(#screenFill)" />
        <rect className="eq e1" x="678.0" y="191.0" width="85.6" height="296.0" rx="42.8" fill="#7B84F2" opacity="0.9" />
        <rect className="eq e2" x="797.6" y="250.2" width="85.6" height="177.6" rx="42.8" fill="#8E7BF2" opacity="0.9" />
        <rect className="eq e3" x="917.2" y="294.6" width="85.6" height="88.8" rx="42.8" fill="#FF3F4A" opacity="0.9" />
        <rect className="eq e4" x="1036.8" y="250.2" width="85.6" height="177.6" rx="42.8" fill="#5FD0B0" opacity="0.9" />
        <rect className="eq e5" x="1156.4" y="191.0" width="85.6" height="296.0" rx="42.8" fill="#33E58A" opacity="0.9" />
      </g>
      <g id="layer-speakers">
        <rect x="230" y="700" width="150" height="118" rx="12" fill="#161616" stroke="#2B2B2B" strokeWidth="2" />
        <circle cx="305" cy="759" r="38" fill="#0B0B0B" stroke="#2B2B2B" strokeWidth="3" />
        <circle cx="305" cy="759" r="14" fill="#1F1F1F" />
        <circle cx="305" cy="759" r="4" fill="#7B84F2" opacity="0.9" />
        <rect x="230" y="574" width="150" height="118" rx="12" fill="#161616" stroke="#2B2B2B" strokeWidth="2" />
        <circle cx="305" cy="633" r="38" fill="#0B0B0B" stroke="#2B2B2B" strokeWidth="3" />
        <circle cx="305" cy="633" r="14" fill="#1F1F1F" />
        <circle cx="305" cy="633" r="4" fill="#7B84F2" opacity="0.9" />
        <rect x="230" y="448" width="150" height="118" rx="12" fill="#161616" stroke="#2B2B2B" strokeWidth="2" />
        <circle cx="305" cy="507" r="38" fill="#0B0B0B" stroke="#2B2B2B" strokeWidth="3" />
        <circle cx="305" cy="507" r="14" fill="#1F1F1F" />
        <circle cx="305" cy="507" r="4" fill="#7B84F2" opacity="0.9" />
        <rect x="230" y="322" width="150" height="118" rx="12" fill="#161616" stroke="#2B2B2B" strokeWidth="2" />
        <circle cx="305" cy="381" r="38" fill="#0B0B0B" stroke="#2B2B2B" strokeWidth="3" />
        <circle cx="305" cy="381" r="14" fill="#1F1F1F" />
        <circle cx="305" cy="381" r="4" fill="#7B84F2" opacity="0.9" />
        <rect x="220" y="716" width="170" height="96" rx="10" fill="#121212" stroke="#242424" strokeWidth="2" />
        <circle cx="271" cy="764" r="26" fill="#0B0B0B" stroke="#2B2B2B" strokeWidth="3" />
        <circle cx="339" cy="764" r="26" fill="#0B0B0B" stroke="#2B2B2B" strokeWidth="3" />
        <rect x="1540" y="700" width="150" height="118" rx="12" fill="#161616" stroke="#2B2B2B" strokeWidth="2" />
        <circle cx="1615" cy="759" r="38" fill="#0B0B0B" stroke="#2B2B2B" strokeWidth="3" />
        <circle cx="1615" cy="759" r="14" fill="#1F1F1F" />
        <circle cx="1615" cy="759" r="4" fill="#33E58A" opacity="0.9" />
        <rect x="1540" y="574" width="150" height="118" rx="12" fill="#161616" stroke="#2B2B2B" strokeWidth="2" />
        <circle cx="1615" cy="633" r="38" fill="#0B0B0B" stroke="#2B2B2B" strokeWidth="3" />
        <circle cx="1615" cy="633" r="14" fill="#1F1F1F" />
        <circle cx="1615" cy="633" r="4" fill="#33E58A" opacity="0.9" />
        <rect x="1540" y="448" width="150" height="118" rx="12" fill="#161616" stroke="#2B2B2B" strokeWidth="2" />
        <circle cx="1615" cy="507" r="38" fill="#0B0B0B" stroke="#2B2B2B" strokeWidth="3" />
        <circle cx="1615" cy="507" r="14" fill="#1F1F1F" />
        <circle cx="1615" cy="507" r="4" fill="#33E58A" opacity="0.9" />
        <rect x="1540" y="322" width="150" height="118" rx="12" fill="#161616" stroke="#2B2B2B" strokeWidth="2" />
        <circle cx="1615" cy="381" r="38" fill="#0B0B0B" stroke="#2B2B2B" strokeWidth="3" />
        <circle cx="1615" cy="381" r="14" fill="#1F1F1F" />
        <circle cx="1615" cy="381" r="4" fill="#33E58A" opacity="0.9" />
        <rect x="1530" y="716" width="170" height="96" rx="10" fill="#121212" stroke="#242424" strokeWidth="2" />
        <circle cx="1581" cy="764" r="26" fill="#0B0B0B" stroke="#2B2B2B" strokeWidth="3" />
        <circle cx="1649" cy="764" r="26" fill="#0B0B0B" stroke="#2B2B2B" strokeWidth="3" />
      </g>

        </>
      ) : layer === 'front' ? (
        <>
      {/*
        The host, front on, behind the decks.

        `layer-booth` draws after this, so the booth masks everything below its
        own top edge - which is why a whole standing figure reads as somebody
        stood behind a table rather than as a person floating in front of one.
        The layer order is load-bearing, not incidental.
      */}
      <g id="layer-dj" transform="translate(817,414) scale(1.3)">
        {/*
          No name over their head.

          The scene shipped with one and it is the wrong place for it twice
          over: this is the host's own profile, so their name is already the
          page, and it is in the status line above the stage as well. A chip
          floating over the decks was a third copy - and the one that covered
          the bottom of the screen behind it.
        */}
        <Character config={host} view="front" crop="full" size={220} anim="bob" />
      </g>

      <g id="layer-booth">
        <rect x="680" y="640" width="560" height="150" rx="16" fill="#161616" stroke="#2B2B2B" strokeWidth="2" />
        <rect x="680" y="640" width="560" height="10" rx="5" fill="#7B84F2" opacity="0.85" />
        <rect className="eq e3" x="880.0" y="698.0" width="14.4" height="74.0" rx="7.2" fill="#7B84F2" />
        <rect className="eq e1" x="916.4" y="712.8" width="14.4" height="44.4" rx="7.2" fill="#8E7BF2" />
        <rect className="eq e4" x="952.8" y="723.9" width="14.4" height="22.2" rx="7.2" fill="#FF3F4A" />
        <rect className="eq e2" x="989.2" y="712.8" width="14.4" height="44.4" rx="7.2" fill="#5FD0B0" />
        <rect className="eq e5" x="1025.6" y="698.0" width="14.4" height="74.0" rx="7.2" fill="#33E58A" />
        <circle cx="800" cy="686" r="30" fill="#0B0B0B" stroke="#3C3C3C" strokeWidth="3" />
        <circle cx="1120" cy="686" r="30" fill="#0B0B0B" stroke="#3C3C3C" strokeWidth="3" />
        <rect x="890" y="668" width="140" height="36" rx="8" fill="#0B0B0B" stroke="#3C3C3C" strokeWidth="2" />
      </g>
      <g id="layer-stage">
        <rect x="180" y="812" width="1560" height="34" rx="8" fill="#1B1B1B" stroke="#2B2B2B" strokeWidth="2" />
        <rect x="220" y="846" width="1480" height="26" rx="6" fill="#121212" />
        <rect x="180" y="808" width="1560" height="6" rx="3" fill="#33E58A" opacity="0.5" />
        <polygon points="180,846 1740,846 1820,920 100,920" fill="#101010" />
        <ellipse cx="960" cy="860" rx="640" ry="46" fill="#7B84F2" opacity="0.10" />
      </g>

      <Decor theme={theme} slot="stage" />

        </>
      ) : layer === 'fans' ? (
        <>
      <g id="layer-fans-mid" opacity="0.82">
        {CROWD_MID.map((spot, index) => {
          const who = crowd[CROWD_FRONT.length + index];

          return who === undefined ? null : (
            <Fan
              key={spot.at}
              at={spot.at}
              config={who.config}
              anim={who.anim ?? 'idle'}
              stagger={spot.stagger}
              name={who.name}
            />
          );
        })}
      </g>

      <g id="layer-fans-front">
        {CROWD_FRONT.map((spot, index) => {
          const who = crowd[index];

          return who === undefined ? null : (
            <Fan
              key={spot.at}
              at={spot.at}
              config={who.config}
              anim={who.anim ?? 'idle'}
              stagger={spot.stagger}
              name={who.name}
            />
          );
        })}
      </g>

      {/* Frontmost, over the real crowd: fog is the one thing a season puts
          between the viewer and the room. */}
      <Decor theme={theme} slot="front" />

        </>
      ) : null}

      {children}
    </svg>
  );
}
